import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { environment } from '@environment';
import { patchState, signalState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { Badge, CreateBadge } from '~/domain/types/badge';
import { Pageable, Paginated, Pagination, Searchable, Sort, Sortable } from '~/domain/types/listings';

type State = {
  q: string;
  sort: Sort;
  pagination: Pagination;
  badges: Badge[] | null;
};

@Service()
export class BadgesService {
  private readonly _http = inject(HttpClient);
  private readonly _state = signalState<State>({
    q: '',
    sort: { property: 'number', direction: 'asc' },
    pagination: { number: 1, size: 10, totalElements: 0, totalPages: 0 },
    badges: null,
  });

  readonly searchQuery = this._state.q;
  readonly sort = this._state.sort;
  readonly pagination = this._state.pagination;
  readonly badges = this._state.badges;

  async loadBadges(options: Partial<Searchable & Pageable & Sortable> = {}): Promise<void> {
    const { q = this.searchQuery(), page = this.pagination(), sort = this.sort() } = options;
    const params = {
      q,
      page: page.number.toString(),
      size: page.size.toString(),
      orderBy: sort.property,
      sort: sort.direction,
    };

    patchState(this._state, { q, sort });

    const { content, ...pagination } = await firstValueFrom(
      this._http.get<Paginated<Badge>>(`${environment.api}/badges`, { params }),
    );
    patchState(this._state, { badges: content, pagination });
  }

  fetchBadge(id: string): Promise<Badge> {
    return firstValueFrom(this._http.get<Badge>(`${environment.api}/badges/${id}`));
  }

  createBadges(badges: CreateBadge[]): Promise<void> {
    return firstValueFrom(this._http.post<void>(`${environment.api}/badges`, { badges }));
  }

  replaceBadge(id: string, uid: string): Promise<Badge> {
    return firstValueFrom(this._http.post<Badge>(`${environment.api}/badges/${id}/replace`, { uid }));
  }

  archiveBadge(id: string): Promise<void> {
    return firstValueFrom(this._http.post<void>(`${environment.api}/badges/${id}/archive`, null));
  }

  restoreBadge(id: string): Promise<void> {
    return firstValueFrom(this._http.post<void>(`${environment.api}/badges/${id}/restore`, null));
  }
}
